import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import AccountsApi from '../../services/api/AccountsApi';
import AuthApi from '../../services/api/AuthApi';
import { HintType, PageType } from '../../services/api/dto/Accounts';
import { LoginData } from '../../services/api/dto/Auth';
import {
  IUserData,
  LoginUserActionProps,
  USERS_ACTION_TYPE,
  USERS_ROLE_TYPE,
} from '../../types';
import { RootState } from '../../types';
import { configureAxios } from '../../utils/configureAxios';
import { showError } from '../../utils/utils';

import { setPush } from './app';

const setToken = createAction<string>('auth/setToken');
const setAuthLoading = createAction<{
  type:
    | 'login'
    | 'getProfileInfo'
    | 'getUserList'
    | 'setUserAction'
    | 'setUserRole'
    | 'createUser'
    | 'updateUser'
    | 'changePassword';
  value: boolean;
}>('auth/setLoading');
const loginUser = createAsyncThunk(
  'auth/loginOLD',
  async (payload: LoginUserActionProps, thunkAPI) => {
    if (payload.onFinish) {
      payload.onFinish();
    }
  }
);
const verifyLoginPhone = createAsyncThunk(
  'auth/verifyPhone',
  (payload: string, thunkApi) => {
    thunkApi.dispatch(setAuthLoading({ value: true, type: 'login' }));
    setTimeout(() => {
      thunkApi.dispatch(setAuthLoading({ value: false, type: 'login' }));
    }, 3000);
  }
);

const getLoginId = createAsyncThunk(
  'auth/getId',
  async (
    {
      phoneNumber,
      password,
      callback,
      errorCallback,
    }: {
      phoneNumber: string;
      password: string;
      callback: (data: LoginData) => void;
      errorCallback: () => void;
    },
    thunkApi
  ) => {
    thunkApi.dispatch(setAuthLoading({ value: true, type: 'login' }));
    try {
      const response = await AuthApi.passwordLogin(phoneNumber, password);
      if (response.status === 200) {
        callback(response.data);
        return response.data;
      }
      return false;
    } catch (e) {
      errorCallback();
      thunkApi.dispatch(
        setPush({ message: 'Неверный логин или пароль', type: 'error' })
      );
      return false;
    } finally {
      thunkApi.dispatch(setAuthLoading({ value: false, type: 'login' }));
    }
  }
);

const setUserData = createAction<IUserData>('auth/setLoginCredentials');

const removeAuthData = createAsyncThunk(
  'auth/removeAuthData',
  async (_: Record<string, unknown>, thunkApi) => {
    configureAxios('');
    const userData = {
      type: 'account',
      value: {
        isLogedIn: false,
        id: 0,
        phoneNumber: '',
        uuid: '',
        token: '',
      },
    } as IUserData;
    thunkApi.dispatch(setUserData(userData));
  }
);

const login = createAsyncThunk(
  'auth/login',
  async (
    {
      code,
      credentials: { id, identifier, userId },
      callback,
    }: {
      code: string;
      credentials: LoginData;
      callback: () => void;
    },
    thunkApi
  ) => {
    thunkApi.dispatch(setAuthLoading({ value: true, type: 'login' }));
    try {
      const response = await AuthApi.login(id, code);
      if (response.status === 200) {
        const accountResponse = await AccountsApi.getAccountInfo(
          response.data.jwt
        );
        if (accountResponse.status === 200) {
          configureAxios(response.data.jwt);
          // alert(`Account Data:${JSON.stringify(accountResponse.data)}`);
          const userData = {
            type: 'account',
            value: {
              isLogedIn: true,
              id: accountResponse.data.id,
              phoneNumber: identifier,
              uuid: id,
              token: response.data.jwt,
            },
          } as IUserData;
          thunkApi.dispatch(setUserData(userData));
          callback();
        }
      }
    } catch (e) {
      thunkApi.dispatch(
        setPush({ message: 'Неверный логин или пароль', type: 'error' })
      );
    } finally {
      thunkApi.dispatch(setAuthLoading({ value: false, type: 'login' }));
    }
  }
);

const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, thunkApi) => {
    const account = (thunkApi.getState() as RootState).auth.account;
    const response = await AuthApi.refreshToken(account.token);
    if (response.status === 200) {
      return response.data;
    }
    return undefined;
  }
);

const getProfileInfo = createAsyncThunk(
  'auth/getProfileInfo',
  async (_, thunkApi) => {
    try {
      thunkApi.dispatch(
        setAuthLoading({ type: 'getProfileInfo', value: true })
      );
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.getProfileInfo(token);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(
        setAuthLoading({ type: 'getProfileInfo', value: false })
      );
    }

    return undefined;
  }
);

const setHintInfo = createAsyncThunk(
  'actions/setHintInfo',
  async ({ type, hint }: { type: HintType; hint: PageType }, thunkApi) => {
    try {
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.setHintInfo(token, { type, hint });
      if (response.status === 200) {
        thunkApi.dispatch(getProfileInfo());
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      //
    }
  }
);

const getUserList = createAsyncThunk(
  'actions/getUserList',
  async (_, thunkApi) => {
    try {
      thunkApi.dispatch(setAuthLoading({ type: 'getUserList', value: true }));
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.getUserList(token);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(setAuthLoading({ type: 'getUserList', value: false }));
    }
  }
);

const setUserAction = createAsyncThunk(
  'actions/setUserAction',
  async (
    { id, action }: { id: number; action: USERS_ACTION_TYPE },
    thunkApi
  ) => {
    try {
      thunkApi.dispatch(setAuthLoading({ type: 'setUserAction', value: true }));
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.setUserAction(token, id, action);
      if (response.status === 200) {
        thunkApi.dispatch(
          setPush({
            message: `Пользователь успешно ${
              action === 'ACTIVE'
                ? 'разблокирован'
                : action === 'DELETE'
                ? 'удален'
                : 'заблокирован'
            }`,
            type: 'success',
          })
        );
        thunkApi.dispatch(getUserList());
        return response.data;
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(
        setAuthLoading({ type: 'setUserAction', value: false })
      );
    }
  }
);

const setUserRole = createAsyncThunk(
  'actions/setUserRole',
  async ({ id, role }: { id: number; role: USERS_ROLE_TYPE }, thunkApi) => {
    try {
      thunkApi.dispatch(setAuthLoading({ type: 'setUserRole', value: true }));
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.setUserRole(token, id, role);
      if (response.status === 200) {
        thunkApi.dispatch(
          setPush({
            message: 'Роль пользователя успешно изменена',
            type: 'success',
          })
        );
        return response.data;
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(setAuthLoading({ type: 'setUserRole', value: false }));
    }
  }
);

const createUser = createAsyncThunk(
  'actions/createUser',
  async (
    {
      name,
      email,
      role,
    }: {
      name: string;
      email: string;
      role: USERS_ROLE_TYPE;
    },
    thunkApi
  ) => {
    try {
      thunkApi.dispatch(setAuthLoading({ type: 'createUser', value: true }));
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const {
        organizationId,
      } = (thunkApi.getState() as RootState).auth.profile;
      const response = await AccountsApi.createUser(
        token,
        organizationId || 0,
        name,
        email,
        role
      );
      if (response.status === 200) {
        thunkApi.dispatch(
          setPush({
            message: 'Пользователь успешно зарегистрирован',
            type: 'success',
          })
        );
        thunkApi.dispatch(getUserList());
        return response.data;
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(setAuthLoading({ type: 'createUser', value: false }));
    }
  }
);

const updateUser = createAsyncThunk(
  'actions/updateUser',
  async (
    {
      fullName,
      email,
      enableOrderNotification,
      enableOnlyConfirmedNotification,
    }: {
      fullName?: string;
      email?: string;
      enableOrderNotification?: boolean;
      enableOnlyConfirmedNotification?: boolean;
    },
    thunkApi
  ) => {
    try {
      thunkApi.dispatch(setAuthLoading({ type: 'updateUser', value: true }));
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.updateUser(
        token,
        fullName,
        email,
        enableOrderNotification,
        enableOnlyConfirmedNotification
      );
      if (response.status === 200) {
        thunkApi.dispatch(
          setPush({
            message: 'Данные профиля успешно обновлены',
            type: 'success',
          })
        );
        thunkApi.dispatch(getProfileInfo());
        return response.data;
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(setAuthLoading({ type: 'updateUser', value: false }));
    }
  }
);

const changePassword = createAsyncThunk(
  'actions/changePassword',
  async (
    {
      password,
      newPassword,
    }: {
      password: string;
      newPassword: string;
    },
    thunkApi
  ) => {
    try {
      thunkApi.dispatch(
        setAuthLoading({ type: 'changePassword', value: true })
      );
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.changePassword(
        token,
        password,
        newPassword
      );
      if (response.status === 200) {
        thunkApi.dispatch(
          setPush({
            message: 'Пароль успешно изменен',
            type: 'success',
          })
        );
        thunkApi.dispatch(getProfileInfo());
        return response.data;
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(
        setAuthLoading({ type: 'changePassword', value: false })
      );
    }
  }
);

export {
  loginUser,
  verifyLoginPhone,
  setAuthLoading,
  getLoginId,
  login,
  setUserData,
  setToken,
  removeAuthData,
  refreshToken,
  getProfileInfo,
  setHintInfo,
  getUserList,
  setUserAction,
  setUserRole,
  createUser,
  updateUser,
  changePassword,
};
